import classNames from 'classnames';
import { endOfDay, isBefore, startOfToday } from 'date-fns';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useTrackAddedShareLinkExpirationDate } from '@air/analytics';
import { type ShortUrlResponse } from '@air/api/types';
import { Button } from '@air/primitive-button';
import { DateInput } from '@air/primitive-date-input';
import { parseAirDateToISO } from '@air/utilities';

import { type ShareUrlResponseWithSource } from '~/components/SharePrivateClipModal/types';
import { UpsellFlashIconButton } from '~/components/SharePrivateClipModalV2/UpsellFlashIconButton';
import { SharePrivateContentPermissionToggle } from '~/components/SharePrivateContentModal/shared/SharePrivateContentPermissionToggle';
import {
  EXPIRATION_DATE_INPUT,
  EXPIRATION_DATE_LABEL,
  EXPIRATION_DATE_SUBMIT_BUTTON,
  EXPIRATION_DATE_TOGGLE,
} from '~/constants/testIDs';
import { SHARE_MODAL_UPSELL_MESSAGE } from '~/constants/upsells';
import { useShareSettings } from '~/hooks/useShareSettings';

export type SharePrivateContentExpirationDateSettingProps = {
  shortIdInfo?: ShortUrlResponse;
  onChangeExpirationDate: (expirationDate: string | null) => Promise<ShareUrlResponseWithSource>;
};

export const SharePrivateContentExpirationDateSetting = ({
  shortIdInfo,
  onChangeExpirationDate,
}: SharePrivateContentExpirationDateSettingProps) => {
  const { expirationDateEnabled } = useShareSettings();
  const [hasExpirationDate, setHasExpirationDate] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const expirationDate = useMemo(
    () => (shortIdInfo?.expirationDate ? parseAirDateToISO(shortIdInfo?.expirationDate) : undefined),
    [shortIdInfo?.expirationDate],
  );
  const [dateValue, setDateValue] = useState<Date | undefined>(expirationDate);
  const shortId = useMemo(() => shortIdInfo?.id, [shortIdInfo?.id]);
  const [errorText, setErrorText] = useState('');
  const { trackAddedShareLinkExpirationDate } = useTrackAddedShareLinkExpirationDate();

  useEffect(() => {
    if (expirationDate) {
      setDateValue(expirationDate);
    }
  }, [expirationDate]);

  useEffect(() => {
    setHasExpirationDate(!!expirationDate);
  }, [setHasExpirationDate, expirationDate]);

  const onSave = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!shortId) return;
      try {
        setIsSubmitting(true);
        trackAddedShareLinkExpirationDate({ shortId });
        await onChangeExpirationDate(dateValue?.toISOString() || null);
        setIsSaved(true);
        setIsSubmitting(false);
      } catch (_error) {
        setIsSubmitting(false);
      }
    },
    [shortId, dateValue, onChangeExpirationDate, trackAddedShareLinkExpirationDate],
  );

  const toggleExpirationDate = async () => {
    const nextHasExpirationDate = !hasExpirationDate;
    if (!!expirationDate && !nextHasExpirationDate && shortId) {
      await onChangeExpirationDate(null);
    }
    setIsSaved(false);
    setDateValue(undefined);
    setErrorText('');
    setHasExpirationDate(nextHasExpirationDate);
  };

  const onDateChange = useCallback(
    (date?: Date | null) => {
      const endOfDate = date ? endOfDay(date) : undefined;
      setDateValue(endOfDate);
      if (endOfDate && isBefore(endOfDate, startOfToday())) {
        setErrorText('Expiration date cannot be set in the past');
      } else {
        setErrorText('');
      }
      if (endOfDate !== expirationDate) setIsSaved(false);
    },
    [expirationDate],
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <SharePrivateContentPermissionToggle
          label="Add an expiration date"
          id="expiration-date"
          checked={hasExpirationDate}
          dataTestId={EXPIRATION_DATE_TOGGLE}
          labelTestId={EXPIRATION_DATE_LABEL}
          isLoading={false}
          disabled={!expirationDateEnabled}
          onCheckedChange={toggleExpirationDate}
        >
          {!expirationDateEnabled && <UpsellFlashIconButton>{SHARE_MODAL_UPSELL_MESSAGE}</UpsellFlashIconButton>}
        </SharePrivateContentPermissionToggle>
      </div>

      {expirationDateEnabled && hasExpirationDate && (
        <form className={classNames('mt-3', errorText ? 'mb-3' : 'mb-0')} onSubmit={onSave}>
          <div className="relative mt-3 flex items-center">
            <DateInput
              size="large"
              testId={EXPIRATION_DATE_INPUT}
              className="w-full"
              name="expiration-date"
              selected={dateValue}
              onChange={(date: Date | [Date, Date] | null) => {
                if (Array.isArray(date)) return;
                onDateChange(date);
              }}
            />
            <Button
              appearance="filled"
              className="ml-3"
              color="grey"
              data-testid={EXPIRATION_DATE_SUBMIT_BUTTON}
              disabled={isSubmitting || isSaved || !!errorText || !dateValue}
              isLoading={isSubmitting}
              size="large"
              type="submit"
            >
              {isSaved ? 'Saved' : 'Save'}
            </Button>
          </div>
          {!!errorText && (
            <p className="mt-1 text-12 text-red-9" data-testid={`${EXPIRATION_DATE_INPUT}-error`}>
              {errorText}
            </p>
          )}
        </form>
      )}
    </div>
  );
};
